import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select } from './ui/select';
import { Input } from './ui/input';
import {
  GREENHOUSE_GASES,
  GWP_REPORTS,
  MASS_UNITS,
  MassUnit,
  GwpReport,
  formatMass,
  fromKg,
  gwpFor,
  toKg,
} from '../lib/gwp';

type Direction = 'to-co2e' | 'from-co2e';

export function GasCalculator() {
  const [direction, setDirection] = useState<Direction>('to-co2e');
  const [gasId, setGasId] = useState('CH4');
  const [report, setReport] = useState<GwpReport>('AR6');
  const [amount, setAmount] = useState('1');
  const [unit, setUnit] = useState<MassUnit>('t');

  const gas = GREENHOUSE_GASES.find((item) => item.id === gasId) ?? GREENHOUSE_GASES[1];
  const gwp = gwpFor(gas, report);
  const reportMeta = GWP_REPORTS.find((item) => item.id === report)!;
  const parsed = Number(amount);
  const amountIsValid = Number.isFinite(parsed) && parsed >= 0;

  const result = useMemo(() => {
    if (!amountIsValid) return null;
    const kg = toKg(parsed, unit);
    if (direction === 'to-co2e') {
      const co2eKg = kg * gwp;
      return {
        value: fromKg(co2eKg, unit),
        unit,
        label: 'CO₂e',
        formula: `${formatMass(parsed)} ${unit} ${gas.formula} × ${gwp} = ${formatMass(fromKg(co2eKg, unit))} ${unit} CO₂e`,
      };
    }
    const gasKg = gwp === 0 ? 0 : kg / gwp;
    return {
      value: fromKg(gasKg, unit),
      unit,
      label: gas.formula,
      formula: `${formatMass(parsed)} ${unit} CO₂e ÷ ${gwp} = ${formatMass(fromKg(gasKg, unit))} ${unit} ${gas.formula}`,
    };
  }, [amountIsValid, parsed, unit, direction, gwp, gas.formula]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Convert a gas into carbon dioxide equivalent (CO₂e), or convert CO₂e back into that gas,
        using IPCC 100-year global warming potentials.
      </p>

      <details className="rounded-lg border border-green-200 bg-white text-sm">
        <summary className="cursor-pointer select-none px-3 py-2 font-medium text-green-900">
          How the rates and conversion work
        </summary>
        <div className="space-y-2 border-t border-green-100 px-3 py-2 text-gray-700">
          <p>
            Each rate is a 100-year global warming potential (GWP) from the IPCC. It is stored in the app,
            not looked up online. AR4 is the 2007 report, AR5 is 2013, and AR6 is 2021. The number means
            how many kilograms of CO₂ have the same warming effect as one kilogram of that gas. Carbon dioxide
            is always 1. Fossil methane in AR6 is 29.8; biogenic methane is 27.2, because fossil methane also
            counts the CO₂ released when it breaks down.
          </p>
          <p>
            Gas to CO₂e multiplies the mass by that rate. CO₂e to gas divides by the same rate. The unit you
            pick (g, kg, t, or lb) is converted to kilograms first, the rate is applied, then the result is
            converted back into the same unit.
          </p>
          <p className="text-xs text-gray-500">
            Example: 1 t fossil CH₄ × 29.8 = 29.8 t CO₂e. The reverse is 29.8 t CO₂e ÷ 29.8 = 1 t CH₄.
          </p>
        </div>
      </details>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setDirection('to-co2e')}
          className={`rounded-lg border px-4 py-3 text-left ${
            direction === 'to-co2e'
              ? 'border-green-700 bg-white shadow-sm'
              : 'border-green-100 bg-green-50 text-gray-700'
          }`}
        >
          <div className="text-sm font-semibold text-gray-900">Gas to CO₂e</div>
          <div className="text-xs text-gray-600 mt-1">How much CO₂e does this amount of gas equal?</div>
        </button>
        <button
          type="button"
          onClick={() => setDirection('from-co2e')}
          className={`rounded-lg border px-4 py-3 text-left ${
            direction === 'from-co2e'
              ? 'border-green-700 bg-white shadow-sm'
              : 'border-green-100 bg-green-50 text-gray-700'
          }`}
        >
          <div className="text-sm font-semibold text-gray-900">CO₂e to gas</div>
          <div className="text-xs text-gray-600 mt-1">How much of this gas equals that CO₂e?</div>
        </button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Conversion</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select
            label="Gas"
            value={gasId}
            onChange={(event) => setGasId(event.target.value)}
            options={GREENHOUSE_GASES.map((item) => ({
              value: item.id,
              label: `${item.name} (${item.formula})`,
            }))}
          />
          <Select
            label="GWP source"
            value={report}
            onChange={(event) => setReport(event.target.value as GwpReport)}
            options={GWP_REPORTS.map((item) => ({ value: item.id, label: item.label }))}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={direction === 'to-co2e' ? `Amount of ${gas.formula}` : 'Amount of CO₂e'}
              type="number"
              min="0"
              step="any"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
            <Select
              label="Unit"
              value={unit}
              onChange={(event) => setUnit(event.target.value as MassUnit)}
              options={MASS_UNITS.map((item) => ({ value: item.id, label: item.label }))}
            />
          </div>

          <p className="text-sm text-gray-600">
            {gas.name} GWP100 ({report}): <span className="font-semibold text-gray-900">{gwp}</span>
            {' '}kg CO₂e per kg {gas.formula}. {reportMeta.source}.
          </p>

          {!amountIsValid && (
            <p className="text-sm text-red-700">Enter a number that is zero or greater.</p>
          )}

          {result && (
            <div className="rounded-lg bg-[#002d04] text-white p-5">
              <p className="text-sm text-green-100">Result</p>
              <p className="mt-1 text-3xl font-semibold">
                {formatMass(result.value)} {result.unit} {result.label}
              </p>
              <p className="mt-3 text-sm text-green-100">{result.formula}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Same gas under each IPCC report</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-2 pr-4 font-medium">Report</th>
                  <th className="py-2 pr-4 font-medium">GWP100</th>
                  <th className="py-2 font-medium">
                    {amountIsValid
                      ? direction === 'to-co2e'
                        ? `${formatMass(parsed)} ${unit} ${gas.formula} equals`
                        : `${formatMass(parsed)} ${unit} CO₂e equals`
                      : 'Example'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {GWP_REPORTS.map((item) => {
                  const value = gwpFor(gas, item.id);
                  const kg = amountIsValid ? toKg(parsed, unit) : 1;
                  const shown = direction === 'to-co2e' ? fromKg(kg * value, unit) : fromKg(kg / value, unit);
                  const shownLabel = direction === 'to-co2e' ? 'CO₂e' : gas.formula;
                  return (
                    <tr key={item.id} className="border-t border-gray-100">
                      <td className="py-2 pr-4">{item.label}</td>
                      <td className="py-2 pr-4">{value}</td>
                      <td className="py-2">
                        {formatMass(shown)} {amountIsValid ? unit : 'kg'} {shownLabel}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-gray-500">
            This calculator compares gases by warming impact. It does not calculate emissions from fuel, electricity, or travel.
            Methane has separate fossil and biogenic factors in AR6 because fossil methane also counts the CO₂ from oxidation.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
